import React, { createContext, useContext, useState } from "react";

const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeContextProvider = ({ children }) => {
  // Initialise from the persisted user object so the correct theme is applied before
  // auth completes. Falls back to dark if no user is cached or the value is absent,
  // so new visitors land on the dark theme by default.
  const [mode, setMode] = useState(() => {
    try {
      const stored = localStorage.getItem("curate_user");
      const user = stored ? JSON.parse(stored) : null;
      return user?.preferences?.darkMode === false ? "light" : "dark";
    } catch {
      return "dark";
    }
  });

  const isTouchDevice = React.useMemo(() => {
    const result =
      window.matchMedia("(pointer: coarse)").matches ||
      navigator.maxTouchPoints > 0;
    return result;
  }, []);

  // Keeps the theme-color meta in sync with the active mode. The initial value is
  // seeded pre-React by an inline script in index.html, so this only handles runtime
  // toggles.
  //
  // In a browser tab the meta is the toolbar colour, so it follows the mode. In the
  // installed Android app it sets only the status-bar ICON tint — the background there
  // is the manifest's baked theme_color (#122425) and can't change at runtime — so it
  // stays pinned dark, or Chrome reads "#ffffff" in light mode and paints dark icons
  // onto the dark bar. iOS ignores this meta in standalone entirely.
  React.useEffect(() => {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    const color = isStandalone || mode === "dark" ? "#122425" : "#ffffff";
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", color);
  }, [mode]);

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setMode, isTouchDevice }}>
      {children}
    </ThemeContext.Provider>
  );
};
